/* eslint-disable no-unused-vars */
/* eslint-disable import/no-cycle */
/* eslint import/no-webpack-loader-syntax: off */
import React, {
    useEffect, useRef, useState,
} from "react";
import { styled, useTheme } from "@mui/material/styles";
import { FormControl, InputLabel } from "@mui/material";
import { Editor as TinyMCE } from "@tinymce/tinymce-react";
import "tinymce/tinymce";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/lists";
import "tinymce/plugins/charmap";
import "tinymce/plugins/anchor";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/wordcount";
import "tinymce/plugins/code";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/media";
import "tinymce/plugins/nonbreaking";
import "tinymce/plugins/table";
import "tinymce/plugins/template";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/preview";

import "tinymce/models/dom/model";

import de from "tinymce-i18n/langs5/de";
import { useDebouncedCallback } from "use-debounce";
import sanitizeHtml from "sanitize-html";
// eslint-disable-next-line import/no-unresolved
import contentUiCss from "!!raw-loader!tinymce/skins/ui/oxide/content.min.css";
// eslint-disable-next-line import/no-unresolved
import contentCss from "!!raw-loader!tinymce/skins/content/default/content.min.css";
import useStore from "../../store";
import BoilerplateDisplayer from "../Boilerplates/BoilerplateDisplayer";

const StyledFormControl = styled(FormControl, {
    shouldForwardProp: (prop) => !["fullHeight", "darkMode", "disabled", "label", "focused"].includes(prop),
})(({
    theme, fullHeight, darkMode, disabled, label, focused,
}) => ({
    height: fullHeight ? "100%" : "",
    display: fullHeight && "grid",
    "& .Mui-disabled": {
        color: `${theme.palette.text.primary}!important`,
        WebkitTextFillColor: "initial",
    },
    margin: 0,

    "& .hide": {
        borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.26)",
        borderRadius: 4,
        minHeight: 50,
        height: fullHeight ? "100% !important" : 200,
        display: "initial!important",
        "& *": {
            visibility: "hidden",
        },
    },
    "& .tox-tinymce": {
        borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.26)",
        borderRadius: 4,
        marginTop: -1,
        visibility: "inherit",
        minHeight: 50,
        height: fullHeight ? `calc(100% - ${label ? 14 : 1}px) !important` : "",
        ...label ? {
            marginTop: 14,
        } : {
            marginTop: 1,
        },
        ...focused && {
            borderColor: "#4594ad",
            borderWidth: 2,
        },
    },
    "& .tox-tinymce:hover": {
        borderColor: disabled ? (darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.26)") : (darkMode ? "rgb(255, 255, 255)" : "rgba(0, 0, 0, 0.87)"),
        ...focused && {
            borderColor: "#4594ad",
            borderWidth: 2,
        },
    },
    "& .tox-toolbar__primary": {
        // color: darkMode ? "#ffffff80!important" : "#222f3e80",
        // background: darkMode ? "#272b30!important" : "#fff!important",
        // borderBottom: "1px solid #ccc",
    },
    "& .tox-tbtn": {
        height: "20px !important",
    },
    "& .tox-editor-header": {
        borderBottom: "1px solid #ccc",
        ...disabled && {
            visibility: "hidden",
        },
    },
    "& .tox-statusbar": {
        bottom: 0,
        right: 0,
        position: "absolute",
        background: "none",
        border: "none",
        "& .tox-statusbar__text-container": {
            display: "none",
        },
    },
    ...label && {
        "& .MuiInputLabel-shrink": {
            fontSize: 12,
            transform: "translate(2px, -7px) scale(1) !important",
        },
    },
    ...disabled && {
        "& .tox-sidebar-wrap": {
            marginTop: -40,
        },
    },
}));

// !!! Editor content styles in /react/public/static/editor_content.css !!! \\

const CustomEditor = React.memo((props) => {
    const {
        label,
        value,
        onChange,
        onBlur,
        disabled,
        required,
        fullHeight,
        inputRef,
        error,
        fullWidth,
        tabId,
        forcedRootBlock,
    } = props;

    const currentValue = useRef(value);

    const theme = useTheme();
    const darkMode = useStore((store) => store.app.darkMode);
    const canUseBoilerplates = useStore((store) => store.app.rights.boilerplates?.rights?.use_boilerplates);

    const [darkModeChanged, setDarkModeChanged] = useState(true);
    const [hide, setHide] = useState(true);
    const [showBoilerplates, setShowBoilerplates] = useState(false);
    const [editorRef, setEditorRef] = useState(null);

    const showBoilerplateHandler = (editor) => {
        setShowBoilerplates(true);
        setEditorRef(editor);
    };

    const insertBoilerplateHandler = (boilerplateValue) => {
        editorRef.insertContent(boilerplateValue, { trim: true });
    };

    useEffect(() => {
        setDarkModeChanged(true);
    }, [darkMode]);

    useEffect(() => {
        if (darkModeChanged) {
            setDarkModeChanged(false);
        }
    }, [darkModeChanged]);

    const [initialValue, setInitialValue] = useState(value);
    const [focused, setFocused] = useState(false);

    const onEditorChange = useDebouncedCallback((newValue, editor) => {
        currentValue.current = newValue;

        if (!editor.isNotDirty) onChange(newValue);
    }, 500);

    useEffect(() => {
        if (value !== currentValue.current) {
            setInitialValue("");
            setTimeout(() => {
                setInitialValue(sanitizeHtml(value, {
                    allowedTags: sanitizeHtml.defaults.allowedTags.concat(["img"]),
                    allowedSchemesByTag: {
                        img: sanitizeHtml.defaults.allowedSchemes.concat(["data", "blob", "cid"]),
                    },
                    allowedAttributes: {
                        "*": ["id", "class", "href", "align", "alt", "center", "bgcolor", "height", "width", "style", "color", "border", "checked",
                            "cols", "colspan", "datetime", "hidden", "label", "max", "maxlength", "min", "rows", "rowspan", "selected",
                            "shape", "size", "span", "start", "type", "disabled", "headers", "list", "cellspacing", "font-color",
                            "background-color", "display", "src", "text-align", "font-family", "target"],
                    },
                }));
            }, 0);
        }

        // TODO: dirty fix to reset initial value on return from sleep mode, to keep the changed content
        const handleWakeUp = () => {
            setInitialValue(value);
            setDarkModeChanged(true);
        };

        window.addEventListener("wakeUp", handleWakeUp);

        return () => window.removeEventListener("wakeUp", handleWakeUp);
    }, [value]);

    // TODO: Farben von der toolbar anpassen in darkMode
    return (
        <div style={{ height: fullHeight && "100%", width: fullWidth && "100%" }}>
            {showBoilerplates && (
                <BoilerplateDisplayer
                    tabId={tabId}
                    onClose={() => setShowBoilerplates(false)}
                    insertBoilerplate={insertBoilerplateHandler}
                />
            )}
            <StyledFormControl
                focused={focused}
                required={required}
                error={error}
                style={{ width: fullWidth && "100%" }}
                fullHeight={fullHeight}
                darkMode={darkMode}
                disabled={disabled}
                label={label}
            >
                {label ? (
                    <InputLabel className={[disabled ? "Mui-disabled" : ""]}>
                        {label}
                    </InputLabel>
                ) : null}
                <div className={hide && "hide"}>
                    {!darkModeChanged ? (
                        <TinyMCE
                            onFocusIn={() => { setFocused(true); }}
                            onFocusOut={() => { setFocused(false); if (onBlur) onBlur(); }}
                            init={{
                                setup: (editor) => {
                                    editor.on("init", () => {
                                        setHide(false);
                                    });
                                    if (canUseBoilerplates) {
                                        editor.ui.registry.addButton("boilerplatesButton", {
                                            icon: "paste-text",
                                            tooltip: "Textbaustein hinzufügen",
                                            onAction(_) {
                                                showBoilerplateHandler(editor);
                                            },
                                        });
                                    }
                                    if (canUseBoilerplates) {
                                        editor.ui.registry.addMenuItem("boilerplatesItem", {
                                            text: "Textbaustein hinzufügen",
                                            icon: "paste-text",
                                            onAction: (_) => {
                                                showBoilerplateHandler(editor);
                                            },
                                        });
                                    }
                                },
                                forced_root_block: forcedRootBlock || "h6",
                                height: fullHeight ? "calc(100% - 14px)" : "200",
                                browser_spellcheck: true,
                                contextmenu: "link image table boilerplatesItem",
                                // contextmenu: "link useBrowserSpellcheck image table",
                                // setup(editor) {
                                //     editor.ui.registry.addMenuItem("useBrowserSpellcheck", {
                                //         text: "Use `Ctrl+Right click` to access spellchecker",
                                //         onAction() {
                                //             editor.notificationManager.open({
                                //                 text:
                                //                     "To access the spellchecker, hold the Control (Ctrl) key and right-click on the misspelt word.",
                                //                 type: "info",
                                //                 timeout: 5000,
                                //                 closeButton: true,
                                //             });
                                //         },
                                //     });
                                //     editor.ui.registry.addContextMenu("useBrowserSpellcheck", {
                                //         update(node) {
                                //             return editor.selection.isCollapsed() ? ["useBrowserSpellcheck"] : [];
                                //         },
                                //     });
                                // },
                                language: "de",
                                language_url: { de },
                                menubar: false,
                                paste_data_images: true,
                                plugins: [
                                    "advlist", "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
                                    "searchreplace", "visualblocks", "fullscreen",
                                    "insertdatetime", "media", "table", "code", "wordcount",
                                ],
                                statusbar: true,
                                toolbar: "undo redo boilerplatesButton   | "
                        + "bold italic underline strikethrough | styles | forecolor backcolor table | alignleft "
                        + "| bullist numlist outdent indent | "
                        + "removeformat | code",
                                entity_encoding: "raw",
                                resize: true,
                                skin: false,
                                content_css: false,
                                content_style: [contentCss, contentUiCss, `
                        body, body *:not(h1):not(h1>span):not(h2):not(h2>span):not(h3):not(h3>span):not(h4):not(h4>span):not(h5):not(h5>span) {
                            font-size: 0.8rem;
                            font-family: "arial";
                        }
                        
                        h1 {
                            font-size: 36px;
                        }
                        
                        h2 {
                            font-size: 30px;
                        }
                        
                        h3 {
                            font-size: 24px;
                        }
                        
                        h4 {
                            font-size: 18px;
                        }
                        
                        h5 {
                            font-size: 14px;
                        }

                        html {
                            height: calc(100% - 16px);
                            color: ${darkMode ? "#ffffff" : "#000000de"};
                            background-color: ${darkMode ? "#272b30" : "#ffffff"};                      
                        }
                        
                        h6{
                            font-weight: 500;
                            margin:10px 0;
                        }
                       
                                                
                        body {
                            margin: 0;
                            padding: 8px;
                            min-height: 100%;
                        }
                        
                        body.mce-content-readonly {
                            color: ${darkMode ? "#ffffff" : "#000000de"};
                            background-color: ${darkMode ? "#414449" : theme.palette.action.disabledBackground};                      
                        }      
                        body.mce-content-readonly *:not(span), body.mce-content-body *:not(span) {
                            color: ${darkMode ? "#ffffff !important" : ""};
                            background: transparent !important;
                        }
                        body.mce-content-readonly a, body.mce-content-body a {
                            color: ${darkMode ? "#4594AD !important" : ""}
                        }
                        body.mce-content-readonly a span, body.mce-content-body a span  {
                            color: ${darkMode ? "#4594AD !important" : ""}
                        }        
                    `].join("\n"),
                                style_formats: [
                                    {
                                        title: "Headings",
                                        items: [
                                            { title: "Heading 1", format: "h1" },
                                            { title: "Heading 2", format: "h2" },
                                            { title: "Heading 3", format: "h3" },
                                            { title: "Heading 4", format: "h4" },
                                            { title: "Heading 5", format: "h5" },
                                        ],
                                    },
                                    {
                                        title: "Blocks",
                                        items: [
                                            { title: "Paragraph", format: "p" },
                                            { title: "Blockquote", format: "blockquote" },
                                            { title: "Div", format: "div" },
                                        ],
                                    },
                                ],
                            }}
                            initialValue={initialValue}
                            onEditorChange={onEditorChange}
                            disabled={disabled}
                            ref={inputRef}
                        />
                    ) : null}
                </div>
            </StyledFormControl>
        </div>
    );
});

export default CustomEditor;
